import { validatePasswordPolicy } from '@pulsify/shared/password'
import {
  BUSINESS_NAME_MAX_LENGTH,
  EMAIL_REGEX,
  PROFILE_NAME_MAX_LENGTH,
} from '@pulsify/shared/validation'
import { z } from 'zod'
import * as m from '~/paraglide/messages'

export const loginSchema = z.object({
  email: z.string().trim().regex(EMAIL_REGEX, m.invalidEmailAddress()),
  password: z.string().min(1, m.passwordRequired()),
})

export const ownerFieldsSchema = z.object({
  firstName: z.string().trim().min(1, m.firstNameRequired()).max(PROFILE_NAME_MAX_LENGTH),
  lastName: z.string().trim().min(1, m.lastNameRequired()).max(PROFILE_NAME_MAX_LENGTH),
  email: z.string().trim().regex(EMAIL_REGEX, m.invalidEmailAddress()),
})

export const businessFieldsSchema = z.object({
  companyName: z.string().trim().min(1, m.tradeOrBrandNameRequired()).max(BUSINESS_NAME_MAX_LENGTH),
  legalName: z.string().trim().min(1, m.legalNameRequired()).max(BUSINESS_NAME_MAX_LENGTH),
})

export const consentSchema = z.object({
  acceptPrivacyPolicy: z.literal(true, {
    message: m.privacyPolicyAcceptanceRequired(),
  }),
  acceptTerms: z.literal(true, {
    message: m.termsAcceptanceRequired(),
  }),
})

export const registerSchema = z.object({
  email: ownerFieldsSchema.shape.email,
  password: z
    .string()
    .min(1, m.passwordRequired())
    .refine((password) => validatePasswordPolicy(password).success, {
      message: m.weakPasswordError(),
    }),
  ...consentSchema.shape,
})

export const forgotPasswordSchema = z.object({
  email: z.string().trim().regex(EMAIL_REGEX, m.invalidEmailAddress()),
})

export const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(1, m.passwordRequired())
      .refine((password) => validatePasswordPolicy(password).success, {
        message: m.weakPasswordError(),
      }),
    confirmPassword: z.string().min(1, m.passwordConfirmationIsRequired()),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: m.passwordsDoNotMatch(),
    path: ['confirmPassword'],
  })

export const businessUserSchema = z.object({
  userId: z.string(),
  email: z.string().pipe(z.email()),
  businessId: z.string(),
  roles: z.string(),
})

export type LoginInput = z.infer<typeof loginSchema>
export type RegisterInput = Pick<z.infer<typeof registerSchema>, 'email' | 'password'>
export type BusinessUser = z.infer<typeof businessUserSchema>
